import React, { useState, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { Copy, BarChart3, AlertTriangle, Calendar, GitBranch, MapPin, MessageSquare, Grid, Search, Link as LinkIcon, Target, Zap } from 'lucide-react';
import './DataQualityDashboard.css';
import { DataService } from '../../services/dataService';

import Tabs from '../layout/Tabs';

// Import sections
import UniquenessSection from './sections/UniquenessSection';
import CompletenessSection from './sections/CompletenessSection';
import OutliersSection from './sections/OutliersSection';
import DurationSection from './sections/DurationSection';
import SurveyFlowDashboard from './sections/SurveyFlowDashboard';
import GeolocationSection from './sections/GeolocationSection';
import TextAnalysisSection from './sections/TextAnalysisSection';
import SentinelPhraseSection from './sections/SentinelPhraseSection';
import GatedFieldSection from './sections/GatedFieldSection';
import ProbeDepthSection from './sections/ProbeDepthSection';
import StraightLiningSection from './sections/StraightLiningSection';
// import IssueHeatmap from './sections/IssueHeatmap';

const DataQualityDashboard = ({ 
  checkResults, 
  data = null,                  // primary survey data (columns + rows) for inline-audio lookups
  projectId = null,             // for fetching audio settings (media_dir)
  isTauri = false,              // whether the app is running inside Tauri
  userInfo = null,              // for re-transcribe audit log author
  dataCollectorId = null,
  activeTab: externalActiveTab = null,
  onTabChange: externalOnTabChange = null,
  isSidePanelExpanded = true,
  selectedVariables = null,
}) => {
  const { isDarkMode } = useTheme();
  const [internalActiveTab, setInternalActiveTab] = useState('');

  // Pluck coaching suggestions out of checkResults so they're forwarded to
  // the three text detector sections rather than rendered as their own tab.
  const coachingSuggestions =
    checkResults?.coaching_suggestions?.suggestions ||
    checkResults?.coaching_suggestions ||
    [];

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  // ── Audio settings (media_dir) for inline play buttons in flagged-rows ───
  // Loaded from /api/projects/{id}/audio-settings, the same endpoint AudioAudit
  // uses. We only need media_dir here. Silently null if not set up — the
  // FlaggedRowAudioButton will hide itself.
  const [audioSettings, setAudioSettings] = useState(null);
  useEffect(() => {
    if (!projectId || !isTauri) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await DataService.getAudioSettings(projectId);
        if (!cancelled) setAudioSettings(settings || null);
      } catch (e) {
        if (!cancelled) setAudioSettings(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, isTauri]);

  // Resolve Tauri API for the InlineAudioCell. Lazy import to avoid breaking
  // the web build (matches the pattern AudioAudit uses).
  const [tauriApi, setTauriApi] = useState(null);
  useEffect(() => {
    if (!isTauri) return;
    let cancelled = false;
    (async () => {
      try {
        const core = await import('@tauri-apps/api/core');
        if (!cancelled) setTauriApi({ invoke: core.invoke });
      } catch (e) {
        if (!cancelled) setTauriApi(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isTauri]);

  // ── Transcripts meta (confidence + segments per Whisper-transcribed cell) ─
  // Uploaded by TranscribeModal after a transcribe job completes. Used by
  // FlaggedRowAudioButton to render confidence pills next to play buttons.
  // Silently null if no transcripts have been run, in which case pills don't
  // render and behavior is identical to pre-Ship-1 (graceful degradation).
  const [transcriptsMeta, setTranscriptsMeta] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const meta = await DataService.getTranscriptsMeta(projectId);
        if (!cancelled) setTranscriptsMeta(meta || null);
      } catch (e) {
        if (!cancelled) setTranscriptsMeta(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ── Audio notes document (Ship 2: for re-transcribe audit log) ───────────
  // Re-transcribing logs the original transcript to audio_notes with
  // tag='language', so we need to know the current state to append.
  const [audioNotesDoc, setAudioNotesDoc] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await DataService.getAudioNotes(projectId);
        if (!cancelled) setAudioNotesDoc(doc || { notes: [], dispositions: {} });
      } catch (e) {
        if (!cancelled) setAudioNotesDoc({ notes: [], dispositions: {} });
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Use external activeTab if provided, otherwise use internal state
  const activeTab = externalActiveTab !== null ? externalActiveTab : internalActiveTab;
  const setActiveTab = externalOnTabChange !== null ? externalOnTabChange : setInternalActiveTab;
  

  // Show heatmap tab only when ≥2 check types have results
  // MUTED: Issue Heatmap disabled for performance — re-enable by uncommenting below
  const activeCheckCount = Object.values(checkResults).filter(Boolean).length;
  // const showHeatmap = activeCheckCount >= 2;
  const showHeatmap = false;

  // Available tabs based on checkResults
  const availableTabs = [
    ...(showHeatmap ? [{
      id: 'heatmap',
      name: 'Issue Heatmap',
      icon: Grid,
      component: null, // rendered inline
      available: true,
    }] : []),
    {
      id: 'duplicates',
      name: 'Uniqueness',
      icon: Copy,
      component: UniquenessSection,
      available: !!checkResults.duplicates
    },
    {
      id: 'missingness',
      name: 'Completeness',
      icon: BarChart3,
      component: CompletenessSection,
      available: !!checkResults.missingness
    },
    {
      id: 'outliers',
      name: 'Outliers',
      icon: AlertTriangle,
      component: OutliersSection,
      available: !!checkResults.outliers
    },
    {
      id: 'duration',
      name: 'Duration',
      icon: Calendar,
      component: DurationSection,
      available: !!checkResults.duration
    },
    {
      id: 'survey_flow',
      name: 'Survey Flow',
      icon: GitBranch,
      component: SurveyFlowDashboard,
      available: !!checkResults.survey_flow
    },
    {
      id: 'geolocation',
      name: 'Geolocation',
      icon: MapPin,
      component: GeolocationSection,
      available: !!checkResults.geolocation
    },
    {
      id: 'text_clustering',
      name: 'Text Analysis',
      icon: MessageSquare,
      component: TextAnalysisSection,
      available: !!checkResults.text_clustering
    },
    {
      id: 'straight_lining',
      name: 'Straight-Lining',
      icon: Zap,
      component: StraightLiningSection,
      available: !!checkResults.straight_lining
    },
    {
      id: 'sentinel_phrases',
      name: 'Sentinel Phrases',
      icon: Search,
      component: SentinelPhraseSection,
      available: !!checkResults.sentinel_phrases
    },
    {
      id: 'gated_fields',
      name: 'Gated Fields',
      icon: LinkIcon,
      component: GatedFieldSection,
      available: !!checkResults.gated_fields
    },
    {
      id: 'probe_depth',
      name: 'Probe Depth',
      icon: Target,
      component: ProbeDepthSection,
      available: !!checkResults.probe_depth
    }
  ].filter(tab => tab.available);

  useEffect(() => {
    // Only set default tab if using internal state management and no tab is selected
    if (externalActiveTab === null && !internalActiveTab && availableTabs.length > 0) {
      setInternalActiveTab(availableTabs[0].id);
    }
    // If external tab is set but doesn't match any available tab (e.g. heatmap was muted),
    // reset to first available tab
    if (externalActiveTab !== null && availableTabs.length > 0 &&
        !availableTabs.some(t => t.id === externalActiveTab) && externalOnTabChange) {
      externalOnTabChange(availableTabs[0].id);
    }
  }, [availableTabs, internalActiveTab, externalActiveTab, externalOnTabChange]);

  if (availableTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96 p-8 text-center">
        <div style={{
          padding: '32px',
          borderRadius: '24px',
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.6) 0%, rgba(13, 14, 14, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: isDarkMode 
            ? '0 20px 60px rgba(0, 0, 0, 0.3)'
            : '0 20px 60px rgba(0, 0, 0, 0.1)',
        }}>
          <AlertTriangle 
            className="w-16 h-16 mx-auto mb-6" 
            style={{ 
              color: isDarkMode ? '#64748B' : '#94A3B8',
              strokeWidth: 1.5 
            }} 
          />
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            letterSpacing: '-0.02em',
            color: isDarkMode ? '#F1F5F9' : '#1E293B',
            marginBottom: '12px'
          }}>
            No Results Available
          </h2>
          <p style={{
            fontSize: '14px',
            color: isDarkMode ? '#94A3B8' : '#64748B',
            marginBottom: '32px',
            maxWidth: '400px'
          }}>
            Run some quality checks or back-check analysis first to see your dashboard results here.
          </p>
          <button
            style={{
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #2B8AFF 0%, #1A6FE0 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease',
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onClick={() => window.history.back()}
          >
            Go to Quality Checks
          </button>
        </div>
      </div>
    );
  }

  const getActiveComponent = () => {
    return availableTabs.find(tab => tab.id === activeTab)?.component;
  };

  const ActiveComponent = getActiveComponent();
  
  return (
    <div 
      className="w-full min-h-screen maindashboard-container" 
      style={{ 
        // backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F5', 
        backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
        marginLeft: '0px',
      }}
    >
      {/* Header with Glass Morphism */}
      <div 
        style={{ 
          background: isDarkMode 
            ? 'linear-gradient(180deg, rgba(23, 23, 27, 0.95) 0%, rgba(23, 23, 27, 0.8) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: isDarkMode ? '0px solid rgba(255, 255, 255, 0.05)' : '0px solid rgba(0, 0, 0, 0.05)',
          boxShadow: isDarkMode 
            ? '0 4px 24px rgba(0, 0, 0, 0.2)'
            : '0 4px 24px rgba(0, 0, 0, 0.04)',
          position: 'sticky', 
          top: '0px',
          zIndex: '999'
        }}
      >
        <div className="px-6 py-4">
          <p 
            style={{ 
              color: isDarkMode ? '#64748B' : '#94A3B8', 
              position: 'fixed', 
              top: '0px', 
              fontSize: '0px' 
            }}
          >
            Analysis results for {Object.keys(checkResults).length} quality check(s)
          </p>
        </div>
        
        {/* Modern Tab Navigation */}
        <div style={{position: 'fixed', top: '0px'}}>
          
          <Tabs 
            tabs={availableTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isSidePanelExpanded={isSidePanelExpanded}
            isMobile={isMobile}
          />
        </div>
        

      </div>

      {/* Tab Content with padding */}
      <div className="flex-1 activeQualityDetector" style={{ padding: isMobile ? '72px 12px 12px' : '88px 32px 32px' }}>
        
        
        {/*
        {activeTab === 'heatmap' && (
          <IssueHeatmap
            checkResults={checkResults}
            selectedVariables={selectedVariables}
            isMobile={isMobile}
          />
        )}
        */}
        {ActiveComponent && activeTab !== 'backcheck' && activeTab !== 'heatmap' && (
          <ActiveComponent 
            data={checkResults[activeTab]} 
            coachingSuggestions={coachingSuggestions}
            dataCollectorId={dataCollectorId}
            isSidePanelExpanded={isSidePanelExpanded}
            isMobile={isMobile}
            primaryData={data}
            audioSettings={audioSettings}
            tauriApi={tauriApi}
            transcriptsMeta={transcriptsMeta}
            projectId={projectId}
            userInfo={userInfo}
            audioNotesDoc={audioNotesDoc}
            onMetaUpdated={setTranscriptsMeta}
            onAudioNotesUpdated={setAudioNotesDoc}
          />
        )}

        {!ActiveComponent && activeTab !== 'backcheck' && activeTab !== 'heatmap' && (
          <div className="flex items-center justify-center min-h-96 p-8 ">
            <div 
              style={{
                textAlign: 'center',
                padding: '32px',
                borderRadius: '20px',
                background: isDarkMode 
                  ? 'rgba(23, 23, 27, 0.6)'
                  : 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <AlertTriangle className="w-12 h-12 text-gray-400 mb-4 mx-auto" />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: isDarkMode ? '#E2E8F0' : '#1E293B',
                marginBottom: '8px'
              }}>
                No Data Available
              </h3>
              <p style={{
                color: isDarkMode ? '#94A3B8' : '#64748B',
                fontSize: '13px'
              }}>
                Unable to load data for the selected tab.
              </p>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default DataQualityDashboard;